import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';

const ProcessStepItem = ({ step, index, totalSteps }) => {
  const [isVisible, setIsVisible] = useState(false);
  const stepRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    if (stepRef.current) {
      observer.observe(stepRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isLast = index === totalSteps - 1;

  return (
    <React.Fragment>
      <div
        ref={stepRef}
        className={`flex-1 max-w-xs mx-auto lg:mx-0 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        // SLOWED DOWN: Doubled from 150ms to 600ms for a much more luxurious pace
        style={{ transitionDelay: `${index * 600}ms` }}
      >
        {/* Step Number */}
        <div className="mb-6">
          <div className="w-16 h-16 bg-[#2d4f4f] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto lg:mx-0 shadow-lg">
            {step.id}
          </div>
        </div>

        {/* Step Content */}
        <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center lg:text-left">{step.step}</h3>
        <p className="text-gray-600 leading-relaxed text-center lg:text-left">{step.description}</p>
      </div>

      {/* Arrow between steps */}
      {!isLast && (
        <div
          className={`hidden lg:flex items-center justify-center px-4 pt-8 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          // MATH ADJUSTED: The arrow now appears exactly halfway between the steps (e.g., Step 1 is 0ms, Arrow is 150ms, Step 2 is 300ms)
          style={{ transitionDelay: `${index * 300 + 150}ms` }}
        >
          <ArrowRight size={32} className="text-gray-300" />
        </div>
      )}
    </React.Fragment>
  );
};

const ProcessSection = ({ process }) => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsHeaderVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) {
      headerObserver.observe(headerRef.current);
    }

    return () => headerObserver.disconnect();
  }, []);

  return (
    <section id="process" className="py-24 lg:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            The <span className="text-[#2d4f4f]">Process</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            A proven process that delivers exceptional results, every time.
          </p>
        </div>

        {/* Process Steps Container */}
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center gap-12 lg:gap-0">
          {process.map((step, index) => (
            <ProcessStepItem
              key={step.id}
              step={step}
              index={index}
              totalSteps={process.length}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;